import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { formatPrismDioptre, PRISM_PATTERN } from '@optiflow/shared';
import {
  PrismBase,
  type CustomerMeasurementSummary,
  type PdType,
  type PrescriptionSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useLocale } from '@/hooks/useLocale';
import { formatDate } from '@/utils/format';
import { apiErrorMessage } from '@/services/apiClient';
import { useCustomerMeasurementsQuery } from '@/modules/measurements/api/measurements.api';
import { useRecordPrescriptionMutation, type EyeInput } from '../api/optical.api';
import { SignedPowerInput } from './SignedPowerInput';

interface PrescriptionFormProps {
  orderId: string;
  /** Enables the saved-measurement picker. Absent for a walk-in order. */
  customerId?: string | null;
  existing: PrescriptionSummary | undefined;
  onSaved: (message: string) => void;
}

interface EyeFields {
  sphere: string;
  cylinder: string;
  axis: string;
  add: string;
  prism: string;
  prismBase: PrismBase | '';
}

const EMPTY: EyeFields = { sphere: '', cylinder: '', axis: '', add: '', prism: '', prismBase: '' };

/**
 * The prescription, as it is read off a paper Rx and typed at the counter.
 *
 * Powers stay decimal strings the whole way to the database — `-2.25` typed here reaches Postgres
 * as exactly −2.25, never via a float. The server re-validates everything, including that a
 * cylinder has an axis.
 *
 * A save replaces the whole prescription, so the form always sends every field it holds — prism and base included, and null for a blank one. Correcting a sphere can then never erase a prism the form did not show.
 */
export function PrescriptionForm({
  orderId,
  customerId,
  existing,
  onSaved,
}: PrescriptionFormProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const [right, setRight] = useState<EyeFields>(EMPTY);
  const [left, setLeft] = useState<EyeFields>(EMPTY);
  const [rightPd, setRightPd] = useState('');
  const [leftPd, setLeftPd] = useState('');
  const [totalPd, setTotalPd] = useState('');
  const [pdType, setPdType] = useState<PdType>('DISTANCE');
  const [sourceMeasurementId, setSourceMeasurementId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const measurements = useCustomerMeasurementsQuery(customerId ?? undefined);

  // A face-only measurement has no PD to offer, and validation data is never offered at all.
  const withPd = (measurements.data ?? []).filter(
    (entry) =>
      !entry.isValidation &&
      // A camera estimate is a reference figure, not a number to grind a lens to. The API refuses it too.
      !entry.referenceOnly &&
      (entry.rightPdMm || entry.leftPdMm || entry.totalPdMm),
  );

  const record = useRecordPrescriptionMutation(orderId);

  useEffect(() => {
    if (!existing) return;

    setRight(toFields(existing.right));
    setLeft(toFields(existing.left));
    setRightPd(existing.rightPdMm ?? '');
    setLeftPd(existing.leftPdMm ?? '');
    setTotalPd(existing.totalPdMm ?? '');
    setPdType(existing.pdType ?? 'DISTANCE');
    setSourceMeasurementId(existing.sourceMeasurementId ?? '');
    setNotes(existing.notes ?? '');
  }, [existing]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const hasPd = Boolean(rightPd.trim() || leftPd.trim() || totalPd.trim());

    record.mutate(
      {
        right: toPayload(right),
        left: toPayload(left),
        // Decimal strings, never numbers: a tenth of a millimetre is not binary-representable.
        rightPdMm: rightPd.trim() || null,
        leftPdMm: leftPd.trim() || null,
        totalPdMm: totalPd.trim() || null,
        // The type travels with a PD and never without one.
        pdType: hasPd ? pdType : null,
        sourceMeasurementId: sourceMeasurementId || null,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => onSaved(t('sales.optical.prescriptionSaved')),
        onError: (caught) =>
          setError(apiErrorMessage(caught) ?? t('sales.optical.prescriptionError')),
      },
    );
  };

  /**
   * Picking a saved measurement fills the PD fields in.
   *
   * It copies rather than links: the reference is recorded for tracing, and the order keeps its own
   * figures forever — a measurement taken next March must not change lenses cut this week.
   */
  const applyMeasurement = (id: string) => {
    setSourceMeasurementId(id);
    if (!id) return;

    const chosen = withPd.find((entry) => entry.id === id);
    if (!chosen) return;

    setRightPd(chosen.rightPdMm ?? '');
    setLeftPd(chosen.leftPdMm ?? '');
    setTotalPd(chosen.totalPdMm ?? '');
    setPdType(chosen.pdType ?? 'DISTANCE');
  };

  // Typing over a copied PD makes it the counter's own figure, so the order stops claiming it came from the measurement.
  const typePd = (set: (value: string) => void) => (value: string) => {
    set(value);
    setSourceMeasurementId('');
  };

  // A trace missing from the list still needs an option, or the select silently shows "by hand": voided since the copy, or the list not loaded yet.
  const traceUnlisted =
    sourceMeasurementId !== '' && !withPd.some((entry) => entry.id === sourceMeasurementId);
  const hasChoices = withPd.length > 0 || traceUnlisted;

  return (
    <Card>
      <CardHeader
        title={t('sales.optical.prescription')}
        subtitle={t('sales.optical.prescriptionHint')}
      />

      <form className="space-y-4 p-4 pt-0" onSubmit={handleSubmit}>
        {error ? <Alert tone="error">{error}</Alert> : null}

        {customerId ? (
          <FormField
            label={t('measurements.picker.label')}
            hint={hasChoices ? t('measurements.picker.hint') : t('measurements.picker.empty')}
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                disabled={!hasChoices}
                options={[
                  { value: '', label: t('measurements.picker.none') },
                  ...withPd.map((entry) => ({
                    value: entry.id,
                    label: `${formatDate(entry.measuredAt, locale)} · ${entry.pdType ? `${t(`measurements.pdTypes.${entry.pdType}`)} · ` : ''}${describePd(entry, t('measurements.total'))} · ${t(`measurements.sources.${entry.source}`)}`,
                  })),
                  ...(traceUnlisted
                    ? [
                        {
                          value: sourceMeasurementId,
                          label: measurements.isSuccess
                            ? t('measurements.picker.voidedSource')
                            : t('common.loading'),
                        },
                      ]
                    : []),
                ]}
                value={sourceMeasurementId}
                onChange={(event) => applyMeasurement(event.target.value)}
              />
            )}
          </FormField>
        ) : null}

        <EyeRow
          label={t('sales.optical.rightEye')}
          values={right}
          onChange={setRight}
          pd={rightPd}
          onPdChange={typePd(setRightPd)}
        />
        <EyeRow
          label={t('sales.optical.leftEye')}
          values={left}
          onChange={setLeft}
          pd={leftPd}
          onPdChange={typePd(setLeftPd)}
        />

        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label={t('sales.optical.totalPd')}
            inputMode="decimal"
            value={totalPd}
            hint={t('sales.optical.totalPdHint')}
            onChange={(event) => typePd(setTotalPd)(event.target.value)}
          />
          <FormField label={t('sales.optical.pdType')}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={pdType}
                options={[
                  { value: 'DISTANCE', label: t('measurements.pdTypes.DISTANCE') },
                  { value: 'NEAR', label: t('measurements.pdTypes.NEAR') },
                ]}
                // A copied PD relabelled by hand is no longer the measurement's, so the trace goes.
                onChange={(event) => {
                  setPdType(event.target.value as PdType);
                  setSourceMeasurementId('');
                }}
              />
            )}
          </FormField>
          <Input
            label={t('sales.optical.notes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" isLoading={record.isPending}>
            <Eye className="size-4" aria-hidden />
            {t('sales.optical.savePrescription')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function EyeRow({
  label,
  values,
  onChange,
  pd,
  onPdChange,
}: {
  label: string;
  values: EyeFields;
  onChange: (next: EyeFields) => void;
  pd: string;
  onPdChange: (next: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <fieldset className="border-line rounded-md border p-3">
      <legend className="text-muted px-1 text-xs font-medium">{label}</legend>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
        <SignedPowerInput
          label={t('sales.optical.sphere')}
          placeholder="-2.25"
          value={values.sphere}
          onChange={(sphere) => onChange({ ...values, sphere })}
        />
        <SignedPowerInput
          label={t('sales.optical.cylinder')}
          placeholder="-0.75"
          value={values.cylinder}
          onChange={(cylinder) => onChange({ ...values, cylinder })}
        />
        <Input
          label={t('sales.optical.axis')}
          inputMode="numeric"
          placeholder="180"
          value={values.axis}
          onChange={(event) => onChange({ ...values, axis: event.target.value })}
        />
        <SignedPowerInput
          signed={false}
          label={t('sales.optical.add')}
          placeholder="+2.00"
          value={values.add}
          onChange={(add) => onChange({ ...values, add })}
        />
        {/* A prism is a magnitude read left to right in every language; its base, not a sign, gives the direction. */}
        <Input
          label={t('sales.optical.prism')}
          dir="ltr"
          inputMode="decimal"
          autoComplete="off"
          placeholder="1.50"
          className="rtl:text-right"
          value={values.prism}
          onChange={(event) => onChange({ ...values, prism: event.target.value })}
          onBlur={() => onChange({ ...values, prism: normalisePrism(values.prism) })}
        />
        <FormField label={t('sales.optical.prismBase')}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={values.prismBase}
              options={[
                { value: '', label: t('sales.optical.noBase') },
                ...Object.values(PrismBase).map((base) => ({
                  value: base,
                  label: t(`sales.optical.bases.${base}`),
                })),
              ]}
              onChange={(event) =>
                onChange({ ...values, prismBase: event.target.value as PrismBase | '' })
              }
            />
          )}
        </FormField>
        <Input
          label={t('sales.optical.pd')}
          inputMode="decimal"
          placeholder="32.5"
          value={pd}
          onChange={(event) => onPdChange(event.target.value)}
        />
      </div>
    </fieldset>
  );
}

/** How one saved measurement reads in the picker: whichever PD values it actually carries. */
function describePd(entry: CustomerMeasurementSummary, totalLabel: string): string {
  return [
    entry.rightPdMm ? `R ${entry.rightPdMm}` : null,
    entry.leftPdMm ? `L ${entry.leftPdMm}` : null,
    entry.totalPdMm ? `${totalLabel} ${entry.totalPdMm}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function toFields(eye: PrescriptionSummary['right']): EyeFields {
  return {
    sphere: eye.sphere ?? '',
    cylinder: eye.cylinder ?? '',
    axis: eye.axis === null ? '' : String(eye.axis),
    add: eye.add ?? '',
    prism: eye.prism ?? '',
    prismBase: eye.prismBase ?? '',
  };
}

/** Every field of the eye, always: a blank box is sent as null, which the server stores as no value — never as a zero. */
function toPayload(fields: EyeFields): EyeInput {
  const axis = fields.axis.trim();

  return {
    sphere: fields.sphere.trim() || null,
    cylinder: fields.cylinder.trim() || null,
    // A whole number of degrees goes as a number; anything else goes as typed so the server refuses it, rather than JSON quietly turning NaN into null.
    axis: axis === '' ? null : /^\d+$/.test(axis) ? Number(axis) : axis,
    add: fields.add.trim() || null,
    prism: fields.prism.trim() || null,
    prismBase: fields.prismBase || null,
  };
}

/** Two decimals and no sign, from integer hundredths. An unreadable value stays exactly as typed, so the server can say what is wrong with it. */
function normalisePrism(value: string): string {
  const trimmed = value.trim();
  return PRISM_PATTERN.test(trimmed) ? (formatPrismDioptre(trimmed) ?? value) : value;
}
